import React, { useEffect, useState } from 'react';
import axios from 'axios';
import firebase from 'firebase/app';
import { BrowserRouter } from 'react-router-dom';
import { AppProvider, IAppState, initialAppState } from './common/AppContext';
import { useRecoilState } from 'recoil';
import { logged } from 'features/Login/store/LoginAtom';
import Routes from './Routes';
import { getAccessToken } from './services/localStorage';
import Loading from 'components/Loading';
import { startAuthMonitoring } from 'utils/authUtils';
import { PortalProvider } from 'common/PortalContext';
import { ExcursionOrganizatorFormProvider } from 'common/ExcursionOrganizatorFormContext';
import { Helmet } from 'react-helmet';
import ScrollToTop from 'components/ScrollToTop/ScrollToTop';

declare global {
  interface Window {
    _env_: {
      API_URL: string;
      FIREBASE_DOMAIN: string;
      FIREBASE_API_KEY: string;
      REACT_APP_INSTAGRAM_ACCESS_TOKEN: string;
      [key: string]: string | undefined;
    };
    firebaseInstance?: firebase.app.App;
  }
}

const App = (): JSX.Element => {
  const [checkingLogin, setCheckingLogin] = useState(true);
  const [checkingCdn, setCheckingCdn] = useState(() => {
    return !document.cookie.includes('Cloud-CDN-Cookie');
  });
  const [appState, setAppState] = useState<IAppState>(initialAppState);
  const [, setLoggedState] = useRecoilState(logged);

  useEffect(() => {
    const checkLogin = () => {
      setCheckingLogin(true);
      const accessToken = getAccessToken();
      if (accessToken) {
        try {
          setAppState({
            ...initialAppState,
            authState: {
              ...initialAppState.authState,
              isAuth: true,
              accessToken,
            },
          });
          setLoggedState(true);
        } catch (error) {
          console.error(error);
        }
      }

      setCheckingLogin(false);
    };

    const fetchCdnAuth = async () => {
      try {
        const apiUrl = window._env_?.API_URL || process.env.REACT_APP_API_URL;
        const response = await axios.get(`${apiUrl}/cdn/cookie`, {
          withCredentials: true,
          timeout: 5000,
        });

        if (response.data && response.data.cookieValue) {
          const domainStr = response.data.domain ? `Domain=${response.data.domain};` : '';
          document.cookie = `Cloud-CDN-Cookie=${response.data.cookieValue}; ${domainStr} Path=/; Secure; SameSite=None; Max-Age=43200`;
        }
      } catch (error) {
        console.error('Erro ao buscar cookie do CDN:', error);
      } finally {
        setCheckingCdn(false);
      }
    };

    checkLogin();
    fetchCdnAuth();
    const intervalId = startAuthMonitoring();
    return () => clearInterval(intervalId);
  }, []);

  if (checkingLogin || checkingCdn) {
    return <></>;
  }

  return (
    <AppProvider initialState={appState}>
      {process.env.NODE_ENV === 'production' && (
        <Helmet>
          {/* Google Analytics */}
          <script async src="https://www.googletagmanager.com/gtag/js?id=G-T5FV5SBVMR" />
          <script>
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-T5FV5SBVMR');
            `}
          </script>

          {/* Google Tag Manager */}
          <script>
            {`
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','GTM-MQJ2K3CP');
            `}
          </script>
        </Helmet>
      )}

      {/* Google Tag Manager (noscript) */}
      {process.env.NODE_ENV === 'production' && (
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-MQJ2K3CP"
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
      )}

      <PortalProvider>
        <ExcursionOrganizatorFormProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes />
          </BrowserRouter>
          <Loading />
        </ExcursionOrganizatorFormProvider>
      </PortalProvider>
    </AppProvider>
  );
};

export default App;
